import React, { useEffect, useState } from 'react';
import { SOLVE_ROWS } from '../../game/presentation';

interface SolveSheetProps {
  open: boolean;
  onClose: () => void;
  onSolve: (guess: string) => void;
}

const SolveSheet: React.FC<SolveSheetProps> = ({ open, onClose, onSolve }) => {
  const [guess, setGuess] = useState('');

  useEffect(() => {
    if (open) {
      setGuess('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const push = (letter: string) => setGuess((value) => `${value}${letter}`);
  const backspace = () => setGuess((value) => value.slice(0, -1));
  const space = () => setGuess((value) => (value.endsWith(' ') || !value ? value : `${value} `));

  const submit = () => {
    if (!guess.trim()) return;
    onSolve(guess.trim());
  };

  const rowClass = (length: number) =>
    length >= 10 ? 'qwerty-10' : length === 9 ? 'qwerty-9' : 'qwerty-7';

  return (
    <div className="ss-overlay" role="dialog" aria-modal="true" aria-labelledby="solve-title">
      <div className="ss-modal ss-solve-sheet">
        <h2 id="solve-title">Solve the puzzle</h2>
        <p className="ss-sub" style={{ textAlign: 'left', marginTop: 0 }}>
          Tap the letters. Punctuation is optional.
        </p>
        <div className="ss-solve-guess" aria-live="polite">
          {guess || '…'}
        </div>
        <div className="ss-keys">
          {SOLVE_ROWS.map((row, index) => (
            <div className={`ss-key-row ${rowClass(row.length)}`} key={`s-${index}`}>
              {row.map((letter) => (
                <button
                  key={letter}
                  type="button"
                  className="ss-key is-on"
                  onClick={() => push(letter)}
                  aria-label={`Type ${letter.toUpperCase()}`}
                >
                  {letter.toUpperCase()}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="ss-actions" style={{ marginTop: '0.7rem' }}>
          <button type="button" className="ss-btn ss-btn-ghost" onClick={backspace}>
            Delete
          </button>
          <button type="button" className="ss-btn ss-btn-ghost" onClick={space}>
            Space
          </button>
          <button type="button" className="ss-btn ss-btn-ghost ss-wide" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ss-btn ss-btn-primary ss-wide" onClick={submit} disabled={!guess.trim()}>
            Solve
          </button>
        </div>
      </div>
    </div>
  );
};

export default SolveSheet;
